import { apiGet, apiPost, apiPatch, apiDelete } from "./apiClient";
import { sourcingSaved } from "../suppliers/supplierRecommendationsModel";

const root = "/vehicle-catalogue-v1";
const unwrap = response => {
  if (!response.data?.success) throw Object.assign(new Error(response.data?.message || "Vehicle catalogue request failed."), { code: response.data?.code, data: response.data?.data });
  return response.data.data;
};
export const vehicleErrorCode = error => error?.response?.data?.code || error?.code;
const makeErrors = {
  MAKE_RENAME_BLOCKED: "This make is in use. Renaming is blocked until the old screens retire; add another spelling instead.",
  DUPLICATE_MAKE: "This make name or spelling already exists. Choose the existing make; it cannot be overridden.",
  POSSIBLE_DUPLICATE: "A similar make already exists. Check the suggestions before saving.",
  VERSION_CONFLICT: "Someone else changed this record. Your edits are kept; reload the saved version before continuing.",
  MAKE_NOT_FOUND: "This make could not be found. Reload the makes list.",
};
export const vehicleErrorMessage = error => error?.response?.status >= 500 ? "Vehicle catalogue is temporarily unavailable. Please try again."
  : error?.response?.status === 403 ? "You do not have permission to manage the vehicle catalogue."
  : makeErrors[vehicleErrorCode(error)] || error?.response?.data?.message || error?.message || "Vehicle catalogue request failed.";
export const isActiveMake = make => Number(make?.status) === 1;
export const vehicleLabel = value => String(value || "").split("_").map(word => word[0]?.toUpperCase() + word.slice(1)).join(" ");
export const reviewStatuses = ["mapped", "needs_review", "unresolved", "ignored"];
export const familyAliasTypes = ["legacy_name", "synonym", "misspelling", "regional", "imported"];
export const fetchCatalogueMakes = () => apiGet(`${root}/makes`).then(unwrap);
export const listManagedMakes = filters => listVehicleCatalogue('makes/manage', filters);
export const getManagedMake = id => apiGet(`${root}/makes/${id}`).then(unwrap);
export const createMake = payload => apiPost(`${root}/makes`, payload).then(unwrap).then(sourcingSaved);
export const updateMake = (id, payload) => apiPatch(`${root}/makes/${id}`, payload).then(unwrap).then(sourcingSaved);
export function deleteCatalogueRecord(kind, id, version) {
  if (!['make', 'model', 'variant'].includes(kind) || !Number.isInteger(Number(id)) || Number(id) < 1 || !Number.isInteger(version) || version < 1) throw new Error('Reload the saved details before deleting.');
  return apiDelete(`${root}/${{ make: 'makes', model: 'families', variant: 'variants' }[kind]}/${id}`, { expected_version: version }).then(unwrap).then(result => {
    if (result?.deleted !== true || Number(result.id) !== Number(id)) throw new Error('Deletion was not confirmed. Reload the saved details before continuing.');
    return sourcingSaved(result);
  });
}
export const makeAliasTypes = ['synonym', 'abbreviation', 'misspelling', 'previous_name', 'regional'];
export function makeWritePayload(values, version) {
  const name = normalized(values.name);
  if (!name) throw new Error('Make name is required.');
  const aliases = (values.aliases || []).map(row => {
    const alias = normalized(row.alias);
    if (!alias || !makeAliasTypes.includes(row.alias_type)) throw new Error('Each alias needs a name and a valid type.');
    return { alias, alias_type: row.alias_type };
  });
  if (version != null && (!Number.isInteger(version) || version < 1)) throw new Error('Reload the make to get its version.');
  if (version != null && !['active', 'inactive'].includes(values.status)) throw new Error('Choose a valid make status.');
  return { name, aliases, confirm_not_duplicate: values.confirm_not_duplicate === true,
    ...(version == null ? {} : { expected_version: version, status: values.status }) };
}
export const makeUsageLine = usage => `${usage?.legacy_models ?? 0} models · ${usage?.customer_vehicles ?? 0} customer vehicles`;
export const makeRenameBlocked = usage => Object.values(usage || {}).some(count => Number(count) > 0);
export const MAKE_RENAME_HINT = makeErrors.MAKE_RENAME_BLOCKED;
export function listVehicleCatalogue(resource, filters = {}) {
  const params = new URLSearchParams();
  ["search", "make_id", "status", "family_id", "page", "limit", "sort"].forEach(key => {
    if (filters[key] != null && filters[key] !== "") params.set(key, String(filters[key]).trim());
  });
  return apiGet(`${root}/${resource}?${params}`).then(unwrap);
}
export const getCatalogueFamily = id => apiGet(`${root}/families/${id}`).then(unwrap);
export const getCatalogueVariant = id => apiGet(`${root}/variants/${id}`).then(unwrap);
export const createCatalogueVariant = body => apiPost(`${root}/variants`, body).then(unwrap).then(sourcingSaved);
export const updateCatalogueVariant = (id, body) => apiPatch(`${root}/variants/${id}`, body).then(unwrap).then(sourcingSaved);
export const linkModelMapping = (id, body) => apiPost(`${root}/legacy-model-mappings/${id}/link`, body).then(unwrap).then(sourcingSaved);
export function variantWritePayload(values, version) {
  if (version != null && (!Number.isSafeInteger(version) || version < 1)) throw new Error('Reload the variant to get its saved version.');
  const name = normalized(values.name);
  const aliases = (values.aliases || []).map(value => normalized(typeof value === 'string' ? value : value.alias));
  if (!name || name.length > 150 || aliases.length > 200 || aliases.some(value => !value || value.length > 150) || new Set(aliases.map(value => value.toLowerCase())).size !== aliases.length) throw new Error('Enter a name and unique aliases, each at most 150 characters.');
  if (!['active', 'hidden'].includes(values.status)) throw new Error('Choose Shown or Hidden.');
  if (version == null && (!Number.isSafeInteger(Number(values.family_id)) || Number(values.family_id) < 1)) throw new Error('Choose a model first.');
  return { name, aliases, status: values.status, ...(version == null ? { family_id: Number(values.family_id) } : { expected_version: version }) };
}
export const createCatalogueFamily = body => apiPost(`${root}/families`, body).then(unwrap).then(sourcingSaved);
export const updateCatalogueFamily = (id, body) => apiPatch(`${root}/families/${id}`, body).then(unwrap).then(sourcingSaved);
export const getModelMapping = id => apiGet(`${root}/legacy-model-mappings/${id}`).then(unwrap);
export const enrollLegacyModel = legacyModelId => apiPost(`${root}/legacy-model-mappings`, { legacy_model_id: legacyModelId, mapping_status: "needs_review", review_notes: null }).then(unwrap);
export const reviewModelMapping = (mappingId, body) => apiPatch(`${root}/legacy-model-mappings/${mappingId}`, body).then(unwrap).then(sourcingSaved);

// A make may have more families than one page. Never silently cap review options.
export async function loadCatalogueFamilyOptions(makeId, search = "", status = "active") {
  const rows = [];
  let page = 1;
  let total;
  do {
    const result = await listVehicleCatalogue("families", { make_id: makeId, search, status, page, limit: 200 });
    rows.push(...result.rows);
    total = result.total;
    if (!result.rows.length) break;
    page += 1;
  } while (rows.length < total);
  return rows.filter(row => (makeId == null || Number(row.make_id) === Number(makeId)) && (status !== "active" || row.status === "active"));
}
const normalized = value => String(value || "").normalize("NFKC").trim().replace(/\s+/g, " ");
export function familyWritePayload(values, version) {
  const canonical_name = normalized(values.canonical_name);
  if (!canonical_name || canonical_name.length > 150) throw new Error("Model name is required and must be at most 150 characters.");
  if ((values.description || "").length > 10000) throw new Error("Description must be at most 10000 characters.");
  if (!["active", "deprecated"].includes(values.status)) throw new Error("Select a valid model status.");
  if (values.aliases.length > 200) throw new Error("A model can have at most 200 aliases.");
  const seen = new Set();
  const aliases = values.aliases.map(row => {
    const alias = normalized(row.alias);
    const key = alias.toLowerCase();
    if (!alias || alias.length > 150 || seen.has(key)) throw new Error("Aliases must be non-empty, unique within this model, and at most 150 characters.");
    if (!familyAliasTypes.includes(row.alias_type)) throw new Error("Select a valid alias type.");
    seen.add(key);
    return { alias, alias_type: row.alias_type };
  });
  if (version == null && (!Number.isInteger(Number(values.make_id)) || Number(values.make_id) <= 0)) throw new Error("Select an active make.");
  return { ...(version == null ? { make_id: Number(values.make_id) } : { expected_version: version }), canonical_name, description: values.description || null, status: values.status, aliases };
}
export function mappingReviewPayload(values, version) {
  if (!reviewStatuses.includes(values.mapping_status)) throw new Error("Select a review decision.");
  if ((values.review_notes || "").length > 10000) throw new Error("Review notes must be at most 10000 characters.");
  const family_id = values.mapping_status === "mapped" ? Number(values.family_id) : null;
  if (values.mapping_status === "mapped" && (!Number.isInteger(family_id) || family_id <= 0)) throw new Error("Select an active model belonging to this make.");
  return { expected_version: version, mapping_status: values.mapping_status, family_id, review_notes: values.review_notes || null };
}
